import {
    useCallback, useEffect, useRef, useState
} from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, shadows, space
} from 'style/config';
import useAuthFlow from 'pages/Auth/hooks/useAuthFlow';
import useOtpSession from 'pages/Auth/SignIn/hooks/useOtpSession';
import useAuthContextRecovery from 'pages/Auth/SignIn/hooks/useAuthContextRecovery';
import useOtpSessionRecovery from 'pages/Auth/SignIn/hooks/useOtpSessionRecovery';
import usePasskeyDetection from 'pages/Auth/SignIn/hooks/usePasskeyDetection';
import CredentialEntryScreen from 'pages/Auth/SignIn/CredentialEntryScreen.f';
import AuthorizationScreen from 'pages/Auth/SignIn/AuthorizationScreen.f';
import OtpEntryScreen from 'pages/Auth/SignIn/OtpEntryScreen.f';
import Logo from 'components/Logo/Logo.f';
import LegalFooter from 'components/Footer/LegalFooter/LegalFooter.f';
import { mediaQueries } from 'style/config';
import store from 'Store';
import userUtils from 'utils/User';
import UserActions from 'actions/UserActions';
import Actions from 'Actions';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// Figma desktop horizontal gutter for the header + footer (px-96). Applied as a
// fixed inset (not a centered Container) so the logo/footer stay at 96px on any
// wide viewport. Mobile uses the 16px space[4] gutter.
const SIDE_GUTTER_DESKTOP = 96;

// The dark-scrim in-flight Loader for the SIM-7 sign-in flow (Figma 270-85083): the
// SAME shared /img/loader.gif spinner as the rest of the app, but on a 25%-black
// scrim (colors.darken3 = rgba(0,0,0,.25), the same overlay token Modal uses)
// instead of the global interstice's 75%-white one. Rendered with hasBg={false}
// inside a fixed full-screen overlay div that paints the dark scrim, so the global
// Interstice is left untouched and this treatment is scoped to sign-in only (FIX 3).

// useOtpSessionRecovery persists a canonical mfaMode value ('phone' | 'email') in its
// whitelisted `method` field (in this flow otpSession.method === mfaMode). On an OTP
// refresh we derive the credentialType from it so OtpEntryScreen's F-001 email
// decision is correct without persisting PII.
const credentialTypeForMode = mfaMode => (mfaMode === 'phone' ? 'PHONE' : 'EMAIL');

/**
 * Auth/SignIn page shell (SIM-1 / ACS-4125).
 *
 * Renders the page landmark, focused H1 heading, and a screen-slot placeholder
 * that reflects the current step from useAuthFlow. Downstream stories will
 * render actual sign-in screens into this slot; per ADR-TS-001 this shell must
 * NOT import the deprecated modal form.
 *
 * SIM-2 (ACS-4126): renders CredentialEntryScreen into the slot when the flow is
 * on the 'credential' screen.
 * SIM-7 (ACS-4131): renders AuthorizationScreen ('authorization') and
 * OtpEntryScreen ('otp'); the lookup result is carried into authContext by
 * CredentialEntryScreen (F-002) and the OTP session lives in useOtpSession.
 */
// Derive the masked destination for a chosen method — masked phone from the
// last-4 only (BR-021), or the already-masked email from the lookup.
const maskedDestinationFor = (mfaMode, authContext) => {
    if (mfaMode === 'phone') {
        return `(***) ***-${authContext?.phoneLast4Digits}`;
    }

    return authContext?.maskedEmail;
};

// AC-023 accessible-name anchor: the id that <main aria-labelledby> points at and
// that route-entry focus lands on. Per-screen heading ownership (Figma) — screens
// whose top heading IS "Sign In" (credential / passkey-detection / authorization)
// let the page shell render the h1; the OTP screen (Figma 270-84837 has NO "Sign In"
// heading) OWNS its own top heading instead, carrying this same id so <main> stays
// labelled and the OTP heading is the focus/label target.
const HEADING_ID = 'signin-heading';

// Screens whose Figma top heading is the page title "Sign In": the shell renders the
// h1 for these. The OTP screen is intentionally absent — it renders its own heading.
const SHELL_HEADING_SCREENS = ['credential', 'passkey-detection', 'authorization', 'disambiguation'];

const SignIn = () => {
    // Single source of truth for the auth-flow state machine. `goTo` is passed
    // down to the mounted screen so navigation updates THIS instance's
    // currentScreen (the one the slot renders against) — screens must not call
    // useAuthFlow() themselves.
    const {
        currentScreen, goTo, completeAuthentication, authContext, setAuthContext
    } = useAuthFlow();
    // Single OTP session for the flow; seeded on method selection and consumed by
    // OtpEntryScreen. Counters persist across method switches (BR-041).
    const otpSession = useOtpSession();
    // AC-AUTH-REFRESH (SIM-7): persist/recover/clear the non-PII authorization-step
    // context so a refresh re-renders the Authorization step (not credential entry).
    const authRecovery = useAuthContextRecovery();
    // SIM-10 OTP recovery (FIX 6): survives a refresh ON THE OTP STEP so the sent code
    // stays valid and is NOT re-sent (BR-028). We use ONLY its pure persist/recover/
    // clear helpers and neutralise its built-in mount effect by passing a no-op
    // navigator — precedence (OTP > authorization > credential) is orchestrated in the
    // single mount effect below so the two recovery mechanisms compose instead of
    // racing. SIM-10's own hook + tests are left completely untouched.
    const otpRecovery = useOtpSessionRecovery(noop);
    // in-flight loader state (FIX 3/7): true while an OTP send/verify is in flight and
    // kept true across the post-verify redirect; drives the dark-scrim shared Loader.
    const [busy, setBusy] = useState(false);
    // Set when the mount effect restored the OTP step from recovery: tells OtpEntryScreen
    // to SKIP its initial send (the previously-sent code stays valid — BR-028, no resend).
    const otpRestoredRef = useRef(false);
    const { status: passkeyStatus, passkeys: detectedPasskeys, DETECTION_STATUS } = usePasskeyDetection();
    const headingRef = useRef(null);
    const hasTriggeredSelector = useRef(false);
    // BR-016 / AC-022 (ADR-SIM9-003, resolved OQ-1 = LOGGED_IN only): set on mount
    // when a fully-authenticated customer was auto-logged-out, so the recovery effect
    // below does NOT restore a stale authorized step onto the fresh sign-in shell.
    const loggedOutOnMountRef = useRef(false);

    // BR-016 auto-logout guard (Figma-less, PRD prd.v2.md:380). A fully LOGGED_IN
    // customer landing on /signin is silently signed out — NO confirmation modal and
    // NO redirect — then shown a fresh sign-in shell. signOut(false, true, true)
    // skips both the confirmation modal and the redirect while running the logout API
    // + session cleanup (UserActions.js:1793). RECOGNIZED / RECOGNIZED_SSI users are
    // left alone (they have no active authenticated session). SSR-safe: the guard runs
    // only inside this browser mount effect, never at module scope (SIM9-P4-05). It is
    // ordered BEFORE the recovery effect so a logout resets to a fresh flow and the
    // recovery restore cannot fight it.
    useEffect(() => {
        if (userUtils.isSignedIn()) {
            loggedOutOnMountRef.current = true;
            store.dispatch(UserActions.signOut(false, true, true));
            // Clear any persisted authorization/OTP recovery so the fresh shell starts
            // at credential entry, not a stale authorized step (SIM9-P4-03).
            authRecovery.clear();
            otpRecovery.clear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (headingRef.current) {
            headingRef.current.focus();
        }
    }, []);

    // Refresh recovery on mount. Precedence is OTP > authorization > credential:
    //   - If SIM-10 OTP-recovery state exists, OTP WINS: rehydrate the otpSession +
    //     authContext (masked, non-PII) and render the OTP step WITHOUT a re-send.
    //   - Else if authorization-recovery state exists, restore the (non-PII, masked)
    //     authContext and render the Authorization step WITHOUT a new lookup/OTP send.
    //   - Else fall through to the default flow (credential entry), untouched.
    useEffect(() => {
        // BR-016: a fresh sign-in after auto-logout must NOT restore a stale
        // authorized step (SIM9-P4-03) — skip recovery entirely in that case.
        if (loggedOutOnMountRef.current) {
            return;
        }

        const otpRecovered = otpRecovery.recover();

        if (otpRecovered) {
            // FIX 6 — restore the OTP step. Only non-PII fields survive a refresh
            // (BR-021): the masked destination, the resend counters, and the canonical
            // mfaMode carried in `method`. The raw credential, otpCode and, per the
            // SIM-10 precedent, the securityToken are NOT persisted.
            //
            // LIMITATION (securityToken): OTP VERIFY needs the securityToken, which is
            // intentionally never persisted (PII-adjacent, SIM-10 whitelist). After an
            // OTP refresh we therefore restore the OTP SCREEN — masked destination shown,
            // the sent code still valid, code-entry available, NO auto-resend — but a
            // verify attempt cannot complete until a token is re-established. This matches
            // SIM-10's intent ('the sent code stays valid') and defers full post-refresh
            // verify to a follow-up that designs token re-hydration without storing PII.
            const restoredMode = otpRecovered.method;
            const restoredMasked = otpRecovered.maskedDestination;
            otpSession.reset({
                method: restoredMode,
                mfaMode: restoredMode,
                maskedDestination: restoredMasked
            });
            setAuthContext({
                mfaMode: restoredMode,
                maskedDestination: restoredMasked,
                credentialType: credentialTypeForMode(restoredMode)
            });
            otpRestoredRef.current = true;
            goTo('otp');

            return;
        }

        const recovered = authRecovery.recover();

        if (recovered && Array.isArray(recovered.availableAuthMethods)) {
            // Restored context is MASKED-only: the raw enteredCredential (PII) was
            // never persisted (BR-021), so it is absent and AuthorizationScreen falls
            // back to the masked credential display.
            setAuthContext(recovered);
            goTo('authorization');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist the non-PII authorization subset whenever we are on the authorization
    // step (covers both the normal advance from credential entry and a re-entry), so
    // a subsequent refresh can restore it. The hook whitelists fields and drops PII.
    useEffect(() => {
        if (currentScreen === 'authorization' && authContext) {
            authRecovery.persist(authContext);
        }
    }, [currentScreen, authContext, authRecovery]);

    // Persist the non-PII OTP subset whenever we are on the OTP step (FIX 6) so a
    // refresh restores the OTP step (not credential entry). The whitelist carries the
    // masked destination, resend counters and the canonical mfaMode (in `method`) —
    // never the raw credential, otpCode or securityToken (BR-021).
    useEffect(() => {
        if (currentScreen === 'otp' && authContext) {
            otpRecovery.persist({
                method: authContext.mfaMode,
                maskedDestination: authContext.maskedDestination,
                resendCount: otpSession.otpSession.resendCount,
                cooldownEndTime: otpSession.otpSession.cooldownEndTime
            });
        }
    }, [currentScreen, authContext, otpSession.otpSession.resendCount, otpSession.otpSession.cooldownEndTime, otpRecovery]);

    // Method selection from AuthorizationScreen: password is handled elsewhere;
    // an OTP method seeds the session's mfaMode/destination and advances to OTP
    // entry, where the initial send fires (send logic stays in one place).
    const handleSelectMethod = useCallback(
        async (mfaMode, extra = {}) => {
            if (mfaMode === 'password') {
                // AC-046 password terminal (SIM-9, Figma 252-79196): route the inline
                // password through the SIM-10 completeAuthentication seam with the
                // resolved-account securityToken. The busy scrim covers the in-flight
                // login; the raw password is passed straight to the action and never
                // logged or persisted (BR-021). completeAuthentication owns the
                // post-success redirect + failure handling (useAuthFlow).
                setBusy(true);

                try {
                    await completeAuthentication({
                        method: 'password',
                        email: authContext?.enteredCredential,
                        password: extra.password,
                        securityToken: authContext?.securityToken,
                        keepSignedIn: authContext?.keepSignedIn
                    });
                } catch (e) {
                    // useAuthFlow surfaces the failure via authError (and rethrows for its
                    // own routing); this branch only needs to clear the busy loader below.
                } finally {
                    setBusy(false);
                }

                return;
            }

            const maskedDestination = maskedDestinationFor(mfaMode, authContext);
            setAuthContext({ ...authContext, mfaMode, maskedDestination });
            otpSession.reset({ method: mfaMode, mfaMode, maskedDestination });
            // Advancing to OTP is a fresh, user-initiated send — NOT a refresh restore.
            otpRestoredRef.current = false;
            // Advancing to OTP: clear authorization-recovery so it can't override the
            // OTP step on a subsequent refresh (precedence + no stale authorization
            // state once the user has moved past it).
            authRecovery.clear();
            goTo('otp');
        },
        [authContext, otpSession, goTo, setAuthContext, authRecovery, completeAuthentication]
    );

    // Navigation handler passed to AuthorizationScreen: leaving the authorization
    // step back to credential entry (Edit / back) clears the authorization-recovery
    // state so a refresh on credential entry does NOT bounce forward to authorization.
    const goToFromAuthorization = useCallback(
        screen => {
            if (screen === 'credential') {
                authRecovery.clear();
            }

            goTo(screen);
        },
        [goTo, authRecovery]
    );

    // Per-screen heading ownership: the shell renders the "Sign In" h1 only for the
    // screens whose Figma top heading is the page title. On the OTP screen the shell
    // h1 is NOT rendered — OtpEntryScreen owns its own top heading (carrying the same
    // HEADING_ID so <main aria-labelledby> still resolves and focus can land on it).
    const showShellHeading = SHELL_HEADING_SCREENS.includes(currentScreen);
    useEffect(() => {
        if (currentScreen !== 'passkey-detection' || hasTriggeredSelector.current) {
            return;
        }

        if (passkeyStatus === DETECTION_STATUS.NONE || passkeyStatus === DETECTION_STATUS.SINGLE) {
            goTo('credential');
        } else if (passkeyStatus === DETECTION_STATUS.MULTIPLE) {
            hasTriggeredSelector.current = true;
            store.dispatch(
                Actions.showPasskeySelectorModal({
                    isOpen: true,
                    passkeys: detectedPasskeys,
                    successCallback: () => {},
                    errback: () => {
                        goTo('credential');
                    },
                    source: 'signinPage'
                })
            );
        }
    }, [currentScreen, passkeyStatus, detectedPasskeys, goTo, DETECTION_STATUS]);

    return (
        <main
            aria-labelledby={HEADING_ID}
            css={styles.page}
        >
            {/* Site header bar: Sephora logo LEFT-aligned with a light bottom
                shadow (Figma header 700:92764). Fixed gutter: 16px mobile →
                96px desktop (Figma header px-96), matching the footer inset. */}
            <header css={styles.header}>
                <Logo />
            </header>
            {/* Centered content column (400px on desktop) per Figma node 270-84077.
                Field fills the full 400px on desktop (no inner side padding);
                16px gutters only on mobile. */}
            <div css={styles.content}>
                {showShellHeading && (
                    <h1
                        id={HEADING_ID}
                        ref={headingRef}
                        tabIndex={-1}
                        css={styles.heading}
                    >
                        {getText('pageTitle')}
                    </h1>
                )}
                <div
                    data-testid='signin-screen-slot'
                    data-current-screen={currentScreen}
                >
                    {/*
                        TEMP (SIM-2): render CredentialEntryScreen for BOTH the
                        'credential' screen AND the initial 'passkey-detection'
                        screen so /signin lands directly on credential entry until
                        SIM-5 introduces real passkey detection; once SIM-5 ships,
                        'passkey-detection' falls back to the interstice loader and
                        this extra condition is removed. data-current-screen stays
                        bound to the real currentScreen (never hardcoded).
                    */}
                    {(currentScreen === 'credential' || currentScreen === 'passkey-detection') && (
                        <CredentialEntryScreen
                            goTo={goTo}
                            setAuthContext={setAuthContext}
                        />
                    )}
                    {currentScreen === 'authorization' && authContext && (
                        <AuthorizationScreen
                            goTo={goToFromAuthorization}
                            methods={authContext.availableAuthMethods}
                            securityToken={authContext.securityToken}
                            phoneLast4Digits={authContext.phoneLast4Digits}
                            maskedEmail={authContext.maskedEmail}
                            enteredCredential={authContext.enteredCredential}
                            credentialType={authContext.credentialType}
                            otpSession={otpSession}
                            onSelectMethod={handleSelectMethod}
                        />
                    )}
                    {/* SIM-9 (Figma 1466-19076): phone disambiguation. When the
                        lookup returns { requiresEmailConfirmation: true } for an
                        ambiguous phone, CredentialEntryScreen emits goTo('disambiguation');
                        the shell re-renders the SAME screen in email-confirmation mode
                        (copy-only delta). The confirming email re-runs the lookup and,
                        on a single-account result, advances to authorization via the
                        same setAuthContext/goTo seam (no bespoke screen, ADR-SIM9-002). */}
                    {currentScreen === 'disambiguation' && (
                        <CredentialEntryScreen
                            mode='email-confirmation'
                            goTo={goTo}
                            setAuthContext={setAuthContext}
                        />
                    )}
                    {currentScreen === 'otp' && authContext && (
                        <OtpEntryScreen
                            goTo={goTo}
                            method={authContext.mfaMode}
                            session={otpSession}
                            authContext={authContext}
                            completeAuthentication={completeAuthentication}
                            onBusyChange={setBusy}
                            skipInitialSend={otpRestoredRef.current}
                            headingId={HEADING_ID}
                            headingRef={headingRef}
                        />
                    )}
                    {currentScreen === 'passkey-detection' && passkeyStatus === DETECTION_STATUS.NONE && <CredentialEntryScreen goTo={goTo} />}
                </div>
            </div>
            {/* SIM-7 in-flight loader (FIX 3 / FIX 7): a CIRCULAR spinner on a DARK
                25%-black scrim (Figma 270-85083), scoped to the sign-in flow — NOT the
                app's flame loader.gif. Shown while an OTP send/verify is in flight and
                kept up across the post-verify redirect. */}
            {busy && (
                <div css={styles.loaderScrim}>
                    <div
                        css={styles.spinner}
                        role='progressbar'
                        aria-label={getText('loadingLabel')}
                    />
                </div>
            )}
            <LegalFooter />
        </main>
    );
};

// AC-023 / SI-3: Emotion class-scoped color rule on the heading so the
// deterministic stylesheet scan finds a WCAG-AA-safe token color. Heading is
// LEFT-aligned in the content column per Figma node 270-84077 (h3 = 16px).
const styles = {
    // Sticky-footer layout: full-height flex column so the LegalFooter is
    // pushed to the bottom of the viewport when content is short, and scrolls
    // normally when content is tall (same minHeight:100vh precedent as
    // Login.f.jsx / ResetPassword.f.jsx).
    page: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 56,
        // Fixed gutter matching Figma: 16px mobile → 96px desktop (header px-96).
        // NOT a centered max-width Container (that grows the gutter on wide
        // viewports); a fixed inset keeps the logo at 96px like the design.
        paddingLeft: space[4],
        paddingRight: space[4],
        boxShadow: shadows.light, // Figma drop-shadow-light (token; 6px blur ~= design 3px)
        [mediaQueries.md]: {
            paddingLeft: SIDE_GUTTER_DESKTOP,
            paddingRight: SIDE_GUTTER_DESKTOP
        }
    },
    content: {
        flex: 1, // grow to fill space between header and footer (sticky footer)
        width: '100%',
        maxWidth: 400,
        margin: '0 auto',
        // 16px gutters on mobile so the field isn't edge-to-edge; on desktop the
        // 400px column holds the full-width field (Figma form width = 400px).
        paddingLeft: space[4],
        paddingRight: space[4],
        paddingTop: space[6],
        [mediaQueries.md]: {
            paddingLeft: 0,
            paddingRight: 0
        }
    },
    heading: {
        // Figma h3: Helvetica Neue Bold 18px. outline:none suppresses the
        // programmatic-focus box (h1 stays tabIndex=-1 + focused for SI-2;
        // only the visible outline is removed — a11y focus is unchanged).
        color: colors.black,
        fontSize: fontSizes['lg-bg'],
        fontWeight: fontWeights.bold,
        marginTop: 0,
        marginBottom: space[4],
        outline: 'none'
    },
    // Fixed full-screen overlay painting the sign-in flow's DARK 25%-black scrim
    // (Figma 270-85083), flex-centering the circular spinner on top. The global
    // Interstice is left untouched (FIX 3).
    loaderScrim: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // Above the page content (incl. the footer, which renders after this in the
        // DOM) so the busy scrim covers the whole sign-in surface and blocks
        // interaction with footer links while a send/verify is in flight.
        zIndex: 'var(--layer-modal)',
        backgroundColor: colors.darken3
    },
    // Circular spinner (Figma 270-85083) — a rotating ring, NOT the app's flame
    // loader.gif. White ring on the dark scrim; a faint track with a solid top arc.
    spinner: {
        '@keyframes signin-spinner-spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
        },
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: colors.white,
        animation: 'signin-spinner-spin 0.8s linear infinite'
    }
};

export default wrapFunctionalComponent(SignIn, 'SignIn');
